import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Download, ShoppingBag, Package, FileIcon, ChevronDown, Zap, AlertCircle, Shield, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { SEO } from "@/components/seo";
import { useAuth } from "@/hooks/use-auth";
import { formatOrderNumber } from "@shared/orderUtils";

type DownloadFileEntry = {
  url: string;
  name: string;
};

interface OrderItem {
  id: number;
  orderId: number;
  productId: number;
  price: string;
  quantity: number;
  product: {
    id: number;
    name: string;
    slug: string;
    image: string | null;
    downloadFile: string | null;
    downloadFiles: string[] | null;
    instructions: string | null;
    instructionImages: string[] | null;
    howToUse: string | null;
  } | null;
}

interface OrderDetail {
  id: number;
  email: string;
  total: string;
  status: string;
  createdAt: string;
  userId: string | null;
  accessCode: string;
  items: OrderItem[];
}

function getStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "completed": return "default";
    case "pending":   return "secondary";
    case "cancelled": return "destructive";
    default:          return "outline";
  }
}

function parseDownloadFiles(raw: string[] | null | undefined): DownloadFileEntry[] {
  if (!raw) return [];
  return raw.map((f) => {
    try {
      const parsed = JSON.parse(f);
      if (parsed && typeof parsed === "object" && parsed.url) return parsed as DownloadFileEntry;
    } catch {}
    return { url: f, name: f.split("/").pop() || "file" };
  });
}

function OrderCard({ order }: { order: OrderDetail }) {
  const [open, setOpen] = useState(false);

  const hasInstructions = order.items.some(
    (item) =>
      item.product?.instructions ||
      (item.product?.instructionImages && item.product.instructionImages.length > 0) ||
      item.product?.howToUse
  );

  return (
    <Card data-testid={`card-order-${order.id}`}>
      {/* ── Always-visible header ── */}
      <div
        className="flex items-start gap-4 p-4 sm:p-5 cursor-pointer select-none"
        onClick={() => setOpen((v) => !v)}
      >
        {/* Order meta */}
        <div className="flex-shrink-0 min-w-[100px]">
          <p className="font-bold text-sm tracking-wide">{formatOrderNumber(order.id)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {new Date(order.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })}
          </p>
          <Badge
            variant={getStatusVariant(order.status)}
            className="mt-1.5 text-[10px] px-1.5 py-0 no-default-active-elevate"
          >
            {order.status}
          </Badge>
        </div>

        {/* Products */}
        <div className="flex-1 min-w-0 space-y-3">
          {order.items.map((item) => {
            const downloadFiles = parseDownloadFiles(item.product?.downloadFiles);
            const hasFiles = downloadFiles.length > 0;

            return (
              <div key={item.id} className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                {/* Thumbnail */}
                {item.product?.image ? (
                  <div className="w-12 h-12 rounded-md overflow-hidden flex-shrink-0 border">
                    <img
                      src={item.product.image}
                      alt={item.product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                    <Package className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">
                    {item.product?.name ?? "Unknown product"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    ${Number(item.price).toFixed(2)}
                  </p>
                </div>

                {/* Download button(s) — stop propagation so clicking doesn't toggle accordion */}
                <div
                  className="flex flex-wrap gap-1.5 flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  {hasFiles ? (
                    downloadFiles.map((df, idx) => (
                      <a
                        key={idx}
                        href={`/api/orders/${order.id}/download/${item.productId}?code=${order.accessCode}&fileIndex=${idx}`}
                        download
                        data-testid={`link-download-${item.productId}-file-${idx}`}
                      >
                        <Button variant="outline" size="sm" className="text-xs gap-1.5">
                          <Download className="w-3.5 h-3.5" />
                          {df.name.length > 20 ? df.name.slice(0, 18) + "…" : df.name}
                        </Button>
                      </a>
                    ))
                  ) : item.product ? (
                    <a
                      href={`/api/orders/${order.id}/download/${item.productId}?code=${order.accessCode}`}
                      download
                      data-testid={`link-download-${item.productId}`}
                    >
                      <Button variant="outline" size="sm" className="text-xs gap-1.5">
                        <Download className="w-3.5 h-3.5" />
                        Download
                      </Button>
                    </a>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {/* Expand chevron (only if there are instructions to show) */}
        {hasInstructions && (
          <ChevronDown
            className={`flex-shrink-0 w-4 h-4 text-muted-foreground transition-transform duration-200 mt-1 ${open ? "rotate-180" : ""}`}
          />
        )}
      </div>

      {/* ── Expanded: instructions + how to use ── */}
      {open && hasInstructions && (
        <div className="border-t px-4 sm:px-5 py-4 space-y-4">
          {order.items.map((item) => {
            const hasItemInstructions =
              item.product?.instructions ||
              (item.product?.instructionImages && item.product.instructionImages.length > 0) ||
              item.product?.howToUse;
            if (!hasItemInstructions) return null;

            return (
              <div key={item.id} className="space-y-4">
                {/* Installation Instructions */}
                {(item.product?.instructions || (item.product?.instructionImages && item.product.instructionImages.length > 0)) && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                      {item.product?.name} — Installation Instructions
                    </p>
                    {item.product?.instructions && (
                      <p
                        className="text-sm whitespace-pre-wrap leading-relaxed"
                        data-testid={`text-instructions-${item.productId}`}
                      >
                        {item.product.instructions}
                      </p>
                    )}
                    {item.product?.instructionImages && item.product.instructionImages.length > 0 && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {item.product.instructionImages.map((img, idx) => (
                          <a
                            key={idx}
                            href={img}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`instruction-image-${item.productId}-${idx}`}
                          >
                            <img
                              src={img}
                              alt={`Instruction step ${idx + 1}`}
                              className="rounded-md border w-full"
                            />
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* How to Use */}
                {item.product?.howToUse && (
                  <div className="rounded-md border border-primary/20 bg-primary/5 p-4">
                    <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-2">
                      How to Use
                    </p>
                    <p
                      className="text-sm whitespace-pre-wrap leading-relaxed"
                      data-testid={`text-how-to-use-${item.productId}`}
                    >
                      {item.product.howToUse}
                    </p>
                  </div>
                )}
              </div>
            );
          })}

          <div className="flex items-center justify-between pt-2 border-t text-sm">
            <span className="text-muted-foreground">Order total</span>
            <span className="font-bold text-primary">${Number(order.total).toFixed(2)}</span>
          </div>
        </div>
      )}
    </Card>
  );
}

type SubStatus = {
  id: number;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  stripeSubscriptionId: string;
  plan: { name: string; price: string; tier: string } | null;
};

function SubscriptionSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: subStatus, isLoading: statusLoading } = useQuery<SubStatus | null>({
    queryKey: ["/api/subscriptions/my"],
  });

  const { data: sierraAccount } = useQuery<{
    sierraUsername: string | null;
    entitlement: {
      status: string;
      authorizationEndDate: string | null;
      adminSuspended: boolean;
    } | null;
  } | null>({
    queryKey: ["/api/sierra/account"],
    enabled: !!subStatus?.plan,
  });

  const { data: downloads, isLoading: downloadsLoading } = useQuery<{
    files: Array<{ url: string; name: string }>;
    plan: { name: string } | null;
  } | null>({
    queryKey: ["/api/subscriptions/downloads"],
    enabled: !!subStatus?.plan,
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/subscriptions/cancel").then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions/my"] });
      toast({ title: "Subscription cancelled", description: "You'll keep access until the end of your billing period." });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to cancel", description: e.message, variant: "destructive" });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/subscriptions/reactivate").then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/subscriptions/my"] });
      toast({ title: "Subscription reactivated", description: "Your subscription will renew as normal." });
    },
    onError: (e: Error) => {
      toast({ title: "Failed to reactivate", description: e.message, variant: "destructive" });
    },
  });

  if (statusLoading || downloadsLoading) return <Skeleton className="h-32 w-full mb-6" />;
  if (!subStatus || !subStatus.plan) return null;

  const { plan, cancelAtPeriodEnd, currentPeriodEnd } = subStatus;
  const files = downloads?.files ?? [];

  const periodEndDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <Card className="mb-8 border-primary/30 bg-primary/5" data-testid="card-subscription-downloads">
      <CardContent className="p-5">
        {/* Header row */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Zap className="w-4 h-4 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-semibold text-base">{plan.name}</h2>
                {cancelAtPeriodEnd ? (
                  <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-600 bg-amber-50 dark:bg-amber-950/30">
                    Cancels {periodEndDate}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs border-green-500/50 text-green-600 bg-green-50 dark:bg-green-950/30">
                    Active
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                ${Number(plan.price).toFixed(2)}/month
                {periodEndDate && !cancelAtPeriodEnd && (
                  <span className="ml-2">· Renews {periodEndDate}</span>
                )}
                {cancelAtPeriodEnd && periodEndDate && (
                  <span className="ml-2">· Access until {periodEndDate}</span>
                )}
              </p>
            </div>
          </div>

          {/* Cancel / Reactivate button */}
          <div className="flex-shrink-0">
            {cancelAtPeriodEnd ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => reactivateMutation.mutate()}
                disabled={reactivateMutation.isPending}
                data-testid="button-reactivate-subscription"
              >
                {reactivateMutation.isPending ? "Reactivating…" : "Reactivate"}
              </Button>
            ) : (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
                    data-testid="button-cancel-subscription"
                  >
                    Cancel Membership
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel your membership?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div className="space-y-3">
                        <p>
                          Your subscription will be cancelled, but you'll keep full access to all downloads until the end
                          of your current billing period
                          {periodEndDate ? <strong> ({periodEndDate})</strong> : ""}.
                        </p>
                        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 text-sm text-amber-700 dark:text-amber-400">
                          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>You can reactivate anytime before that date to keep your subscription going.</span>
                        </div>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Membership</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => cancelMutation.mutate()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      data-testid="button-confirm-cancel"
                    >
                      {cancelMutation.isPending ? "Cancelling…" : "Yes, Cancel"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Sierra Chart access status */}
        {sierraAccount !== undefined && (
          <div className="flex items-center gap-3 rounded-md border border-border/60 bg-background/50 px-4 py-3 mb-3">
            <Shield className="w-4 h-4 text-primary flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                Sierra Chart Access
              </p>
              {sierraAccount?.sierraUsername ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm font-semibold">{sierraAccount.sierraUsername}</span>
                  {sierraAccount.entitlement?.adminSuspended ? (
                    <Badge variant="destructive" className="text-xs">Suspended</Badge>
                  ) : sierraAccount.entitlement?.status === "authorized" ? (
                    <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                      <CheckCircle className="w-3 h-3" />
                      Active
                      {sierraAccount.entitlement.authorizationEndDate && (
                        <span className="text-muted-foreground font-normal">
                          · through {new Date(sierraAccount.entitlement.authorizationEndDate).toLocaleDateString()}
                        </span>
                      )}
                    </span>
                  ) : (
                    <Badge variant="outline" className="text-xs">Syncing…</Badge>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Not linked —</span>
                  <Link href="/settings" className="text-sm text-primary underline underline-offset-2">
                    add your Sierra Chart account name
                  </Link>
                  <span className="text-xs text-muted-foreground">to activate indicators</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Download files */}
        {files.length === 0 ? (
          <p className="text-sm text-muted-foreground">No downloadable files attached to this plan yet.</p>
        ) : (
          <div className="space-y-1 border-t pt-4 mt-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              Subscription Downloads
            </p>
            {files.map((f, idx) => (
              <a
                key={idx}
                href={f.url}
                download={f.name}
                className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/60 transition-colors group"
                data-testid={`link-subscription-download-${idx}`}
              >
                <FileIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm flex-1 min-w-0 truncate">{f.name}</span>
                <Download className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </a>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PurchasesPage() {
  const { user, isLoading: authLoading } = useAuth();

  const { data: orders, isLoading: ordersLoading } = useQuery<OrderDetail[]>({
    queryKey: ["/api/orders/my"],
    enabled: !!user,
  });

  if (authLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Purchase History" description="View your order history and download your purchased templates and indicators." noIndex />
        <Skeleton className="h-9 w-56 mb-8" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <div className="p-5 flex items-center gap-4">
                <Skeleton className="w-24 h-14" />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-12 h-12 rounded-md" />
                    <Skeleton className="h-5 w-48" />
                  </div>
                </div>
                <Skeleton className="h-8 w-24" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SEO title="Purchase History" description="Sign in to view your order history." noIndex />
        <div className="max-w-md mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Sign In Required</h1>
          <p className="text-muted-foreground mb-6">Please sign in to view your purchase history.</p>
          <Link href="/auth?redirect=/purchases">
            <Button data-testid="button-sign-in">Sign In</Button>
          </Link>
        </div>
      </div>
    );
  }

  if (ordersLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SEO title="Purchase History" description="View your order history and download your purchased templates and indicators." noIndex />
        <h1 className="text-2xl sm:text-3xl font-bold mb-8" data-testid="text-purchases-title">
          Purchase History
        </h1>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <div className="p-5 flex items-center gap-4">
                <Skeleton className="w-24 h-14" />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3">
                    <Skeleton className="w-12 h-12 rounded-md" />
                    <Skeleton className="h-5 w-48" />
                  </div>
                </div>
                <Skeleton className="h-8 w-24" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const hasOrders = orders && orders.length > 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO title="Purchase History" description="View your order history and download your purchased templates and indicators." noIndex />

      <h1 className="text-2xl sm:text-3xl font-bold mb-8" data-testid="text-purchases-title">
        Purchase History
      </h1>

      <SubscriptionSection />

      {!hasOrders ? (
        <div className="max-w-md mx-auto text-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground mb-6" data-testid="text-no-purchases">
            No purchases yet. Start browsing our templates and indicators!
          </p>
          <Link href="/">
            <Button data-testid="button-browse-products">Browse Products</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </div>
      )}
    </div>
  );
}
